import { slugify } from "./utils";

export const STATE_TO_UF = {
  acre: "AC",
  alagoas: "AL",
  amapa: "AP",
  amazonas: "AM",
  bahia: "BA",
  ceara: "CE",
  "distrito federal": "DF",
  "espirito santo": "ES",
  goias: "GO",
  maranhao: "MA",
  "mato grosso": "MT",
  "mato grosso do sul": "MS",
  "minas gerais": "MG",
  para: "PA",
  paraiba: "PB",
  parana: "PR",
  pernambuco: "PE",
  piaui: "PI",
  "rio de janeiro": "RJ",
  "rio grande do norte": "RN",
  "rio grande do sul": "RS",
  rondonia: "RO",
  roraima: "RR",
  "santa catarina": "SC",
  "sao paulo": "SP",
  sergipe: "SE",
  tocantins: "TO",
};

export function foldName(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function compactSlug(s) {
  return slugify(s).replace(/-/g, "");
}

export function padIbge(raw) {
  const d = String(raw ?? "").replace(/\D+/g, "");
  if (!d || /^0+$/.test(d)) return "";
  return d.padStart(7, "0");
}

export function cityUfKey(city, uf) {
  const name = foldName(city);
  const state = String(uf || "").trim().toLowerCase();
  if (!name || !state) return "";
  return `${name}|${state}`;
}

/** Grafias do CSV de CEP / planilha Scania → chave oficial do IBGE. Manter alinhado à API. */
export const CITY_UF_ALIASES = {
  "amparo da serra|mg": "amparo do serra|mg",
  "dona euzebia|mg": "dona eusebia|mg",
  "arez|rn": "ares|rn",
  "boa saude|rn": "januario cicco boa saude|rn",
  "januario cicco|rn": "januario cicco boa saude|rn",
  "graccho cardoso|se": "gracho cardoso|se",
  "tabocao|to": "fortaleza do tabocao|to",
  "itapage|ce": "itapaje|ce",
  "santa isabel do para|pa": "santa izabel do para|pa",
  "florinea|sp": "florinia|sp",
  "sao luis do paraitinga|sp": "sao luiz do paraitinga|sp",
  "eldorado dos carajas|pa": "eldorado do carajas|pa",
  "brasopolis|mg": "brazopolis|mg",
  "iguaraci|pe": "iguaracy|pe",
};

export function resolveCityUfKey(city, uf) {
  const key = cityUfKey(city, uf);
  if (!key) return "";
  return CITY_UF_ALIASES[key] || key;
}

/**
 * "ARIQUEMES (RONDÔNIA)" → cidade + UF.
 * Usa o último parêntese como estado (o resto pode ter texto extra).
 */
export function parseAreaName(raw) {
  const s = String(raw || "").replace(/\s+/g, " ").trim();
  if (!s) return { city: "", state: "", uf: "", raw: "" };
  const m = s.match(/^(.*)\(([^)]+)\)\s*$/);
  if (!m) return { city: s, state: "", uf: "", raw: s };
  const city = m[1].trim().replace(/[–-]\s*$/, "").trim();
  const state = m[2].trim();
  const uf = STATE_TO_UF[foldName(state)] || (/^[A-Z]{2}$/i.test(state) ? state.toUpperCase() : "");
  return { city, state, uf, raw: s };
}

function normHeader(s) {
  return foldName(s).replace(/\s+/g, "");
}

export function detectConcessionColumns(headers) {
  const idx = { ibge: -1, area: -1, dealer: -1, district: -1 };
  (headers || []).forEach((h, i) => {
    const n = normHeader(h);
    if (idx.ibge < 0 && (n === "id" || n.includes("ibge") || n === "codigo" || n === "codigomunicipio")) {
      idx.ibge = i;
    } else if (
      idx.area < 0 &&
      (n.includes("administrativearea") ||
        n.includes("municipio") ||
        n === "cidade" ||
        n === "localidade" ||
        n === "nome")
    ) {
      idx.area = i;
    } else if (
      idx.dealer < 0 &&
      (n.includes("dealerdistrictname") || n === "marca" || n === "dealer" || n === "concessionaria" || n === "rede")
    ) {
      idx.dealer = i;
    } else if (
      idx.district < 0 &&
      (n.includes("servicedistrictname") ||
        n === "filial" ||
        n === "destino" ||
        n === "distrito" ||
        n.includes("unidade"))
    ) {
      idx.district = i;
    }
  });
  return idx;
}

export function parseConcessionRows(aoa) {
  const rows = Array.isArray(aoa) ? aoa : [];
  if (!rows.length) {
    return { rows: [], columns: detectConcessionColumns([]), errors: ["Planilha vazia."] };
  }
  const header = rows[0].map((c) => String(c ?? "").trim());
  const columns = detectConcessionColumns(header);
  const errors = [];
  if (columns.ibge < 0) errors.push("Não achei a coluna de código IBGE (ID).");
  if (columns.area < 0) errors.push("Não achei a coluna de município (AdministrativeAreaName).");
  if (columns.dealer < 0) errors.push("Não achei a coluna da marca/concessão (DealerDistrictName).");
  if (columns.district < 0) errors.push("Não achei a coluna da filial (ServiceDistrictName).");
  if (errors.length) return { rows: [], columns, errors };

  const out = [];
  for (let i = 1; i < rows.length; i += 1) {
    const line = rows[i] || [];
    const ibge = padIbge(line[columns.ibge]);
    const area = parseAreaName(line[columns.area]);
    const dealer = String(line[columns.dealer] ?? "").trim();
    const district = String(line[columns.district] ?? "").trim();
    if (!ibge && !area.city && !dealer) continue;
    out.push({
      ibge,
      city: area.city,
      uf: area.uf,
      state: area.state,
      areaRaw: area.raw,
      dealer,
      district,
      row: i + 1,
    });
  }
  return { rows: out, columns, errors: [] };
}

/** Cabeçalhos da planilha Scania de área de atuação (Cidades). CEP não entra. */
export const CONCESSION_EXAMPLE_HEADERS = [
  "ID",
  "AdministrativeAreaName",
  "ServiceDistrictId",
  "ServiceDistrictName",
  "DealerDistrictId",
  "DealerDistrictName",
];

export const CONCESSION_EXAMPLE_FILE_NAME = "exemplo-area-atuacao-concessoes.xlsx";

export function concessionExampleAoa() {
  return [
    CONCESSION_EXAMPLE_HEADERS,
    ["3552205", "SOROCABA (SÃO PAULO)", "59", "CODEMA SOROCABA", "1", "CODEMA"],
    ["4310207", "IJUÍ (RIO GRANDE DO SUL)", "113", "BRASDIESEL IJUÍ", "2", "BRASDIESEL"],
    ["2611606", "RECIFE (PERNAMBUCO)", "201", "COVEPE RECIFE", "3", "COVEPE"],
  ];
}

export function uniqueDealers(sheetRows) {
  const s = new Set();
  for (const r of sheetRows || []) if (r.dealer) s.add(r.dealer);
  return [...s].sort((a, b) => a.localeCompare(b, "pt"));
}

export function uniqueDistricts(sheetRows, dealer) {
  const s = new Set();
  for (const r of sheetRows || []) {
    if (dealer && r.dealer !== dealer) continue;
    if (r.district) s.add(r.district);
  }
  return [...s].sort((a, b) => a.localeCompare(b, "pt"));
}

function slugScore(query, candidate) {
  const q = compactSlug(query);
  const c = compactSlug(candidate);
  if (!q || !c) return 0;
  if (q === c) return 100;
  if (c.startsWith(q) || q.startsWith(c)) return 80 + Math.min(q.length, c.length);
  if (c.includes(q) || q.includes(c)) return 60 + Math.min(q.length, c.length);
  return 0;
}

export function matchDealer(dealerName, regionKeys = []) {
  const keys = (regionKeys || []).filter(Boolean);
  if (!dealerName || !keys.length) return "";
  let best = "";
  let bestScore = 0;
  for (const k of keys) {
    const sc = slugScore(dealerName, k);
    if (sc > bestScore) {
      bestScore = sc;
      best = k;
    }
  }
  return bestScore >= 60 ? best : "";
}

function stripDealerPrefix(districtName, dealerName) {
  const d = compactSlug(districtName);
  const p = compactSlug(dealerName);
  if (p && d.startsWith(p) && d.length > p.length) return d.slice(p.length);
  return d;
}

export function matchDistrict(districtName, dealerName, targets = []) {
  const list = [...new Set((targets || []).map((t) => String(t || "").trim()).filter(Boolean))];
  if (!districtName || !list.length) return "";
  const full = compactSlug(districtName);
  const rest = stripDealerPrefix(districtName, dealerName);
  let best = "";
  let bestScore = 0;
  for (const t of list) {
    const c = compactSlug(t);
    let sc = 0;
    if (c === full || c === rest) sc = 100;
    else if (rest && rest.length >= 3 && c.endsWith(rest)) sc = 90;
    else if (rest && rest.length >= 4 && c.includes(rest)) sc = 80;
    else if (full && c.includes(full)) sc = 70;
    else if (rest && rest.length >= 4 && rest.includes(c) && c.length >= 6) sc = 65;
    if (sc > bestScore) {
      bestScore = sc;
      best = t;
    }
  }
  return bestScore >= 65 ? best : "";
}

export function suggestDealerMap(sheetRows, regionKeys) {
  const map = {};
  for (const d of uniqueDealers(sheetRows)) map[d] = matchDealer(d, regionKeys);
  return map;
}

export function suggestDistrictMap(sheetRows, dealerMap, targetsByRegion) {
  const map = {};
  const byDealer = new Map();
  for (const r of sheetRows || []) {
    if (!r.district) continue;
    if (!byDealer.has(r.dealer)) byDealer.set(r.dealer, new Set());
    byDealer.get(r.dealer).add(r.district);
  }
  for (const [dealer, districts] of byDealer) {
    const region = dealerMap?.[dealer] || "";
    const targets = region ? targetsByRegion?.[region] || [] : [];
    for (const district of districts) {
      map[district] = matchDistrict(district, dealer, targets);
    }
  }
  return map;
}

/** Preserva escolhas manuais (valor preenchido) e preenche o restante com o palpite. */
export function mergeDistrictSuggestions(prev, suggested, skipped = []) {
  const next = { ...(suggested || {}) };
  for (const [k, v] of Object.entries(prev || {})) {
    if (v) next[k] = v;
  }
  for (const k of skipped || []) {
    if (k) next[k] = "";
  }
  return next;
}

/** Salvo/manual (e exclusões) vencem o palpite automático. */
export function mergeDealerMap(suggested, saved, skippedDealers = []) {
  const next = { ...(suggested || {}) };
  for (const [k, v] of Object.entries(saved || {})) {
    if (v) next[k] = v;
  }
  for (const k of skippedDealers || []) {
    if (k) next[k] = "";
  }
  return next;
}

export function cityCountByDealer(sheetRows) {
  const out = {};
  for (const r of sheetRows || []) {
    if (!r.dealer) continue;
    out[r.dealer] = (out[r.dealer] || 0) + 1;
  }
  return out;
}

export function targetsByRegionFromRanges(cepRanges) {
  const out = {};
  for (const r of cepRanges || []) {
    const region = String(r.region_key || "").trim();
    const target = String(r.target_cod_interno || "").trim();
    if (!region || !target) continue;
    if (!out[region]) out[region] = [];
    if (!out[region].includes(target)) out[region].push(target);
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => a.localeCompare(b, "en"));
  return out;
}
